import { forkJoin, Observable, of as observableOf, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as jwt_decode from 'jwt-decode';
import _isString from 'lodash-es/isString';
import { Yii } from "libraries/interfaces/Yii";
import { IRequester, ISamContact } from "libraries/interfaces/IUser";
import { wenji } from "libraries/interfaces/IWenji";
import { AuthRedirectService } from "libraries/service/auth-redirect/auth-redirect.service";
import { SamResourceService } from '../resources/sam/samResource.service';
import { OsmanCompanyResource } from '../resources/osman/osman-company.resource';
import { ISamAgent } from '../resources/sam/ISamAgent';
import { IOsmanCompany } from '../resources/osman/osman';
import { UserAuthService } from '../user-auth/user-auth.service';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { InstanceService } from '../instance/instance.service';
import {
    AfidiumConnect,
    IAfidiumConnectSamResponse,
    IAfidiumConnectConfig, IAfidiumConnectOAuthResponse
} from "../../../app/shared/model/afidium-connect.model";

export interface IResponse {
    data: {};
    display: IResponseDisplay;
}

export interface IResponseDisplay {
    message: string;
}

declare var xtoken: string; //use in wtb

@Injectable()
export class AuthService {
    public afidiumConnect: AfidiumConnect;
    public response: IResponse = {data: {}, display: {message: ''}};
    public osmanRequesterCache: IRequester;
    //storage of asked url before loggin
    public redirectUrl: string;
    public url: string;
    // private instance variable to hold API url
    public xsecurityPath: string = `${sessionStorage.getItem("$wui_url")}config/xsecurity.php`;
    public authPath: string = `${sessionStorage.getItem("$instance_url")}api/users/authenticate`;
    private isLogged: boolean;
    private readonly lsPrefix: string = sessionStorage.getItem("$instance_url");
    private errorLoginMessageSubject: Subject<string> = new Subject<string>();
    public errorLoginMessageObservable: Observable<string> = this.errorLoginMessageSubject.asObservable();
    constructor(
        protected readonly http: HttpClient,
        private readonly router: Router,
        private readonly userAuthService: UserAuthService,
        private readonly authRedirectService: AuthRedirectService,
        private readonly osmanCompanyResource: OsmanCompanyResource,
        private readonly samResourceService: SamResourceService,
        private readonly instanceService: InstanceService,
    ) {
    }

    public get isLoggedIn(): boolean {
        return this.isLogged;
    }

    public get userContact(): ISamContact {
        return this.userAuthService.userInformations.sam_contact;
    }

    public login(model: any, ip: boolean = false): void {
        //encode to xsecurity, then send to getToken
        if (ip) {
            this.getXsecurityIP();
        } else {
            this.getXsecurity(model);
        }
    }

    public initAfidiumConnect(): void {
        const afidiumConnectConfig: IAfidiumConnectConfig = wenji?.connect ?? null;
        this.afidiumConnect = new AfidiumConnect(afidiumConnectConfig);
        const afidiumConnectOAuthResponse: IAfidiumConnectOAuthResponse = {
            access_token: this.userAuthService.getStorageOAuthToken() ?? null,
            token_type: this.userAuthService.getStorageOAuthTokenType() ?? null
        };
        this.afidiumConnect.setOAuthResponse(afidiumConnectOAuthResponse);
    }

    public loginAfidiumConnect(): Observable<void> {
        let headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json');
        return this.http.post(this.afidiumConnect.getOAuthUrl(), this.afidiumConnect.getOAuthRequest(), { headers }).pipe(
            catchError((err: any): Observable<void> => {
                this.afidiumConnect.setApiError(err?.error?.error);
                return of();
            }),
            mergeMap((afidiumConnectOAuthResponse: any): Observable<void> => {
                this.afidiumConnect.setOAuthResponse(afidiumConnectOAuthResponse);
                if (this.afidiumConnect.isOAuthResponseValid()) {
                    headers = headers.append('Authorization', this.afidiumConnect.getSamAuthorizationHeader());
                    return this.http.get(this.afidiumConnect.getSamUrl(), { headers }).pipe(
                        catchError((err: any): Observable<void> => {
                            this.afidiumConnect.setApiError(err?.error?.error);
                            return of();
                        }),
                        map((afidiumConnectSamResponse: IAfidiumConnectSamResponse): void => {
                            this.afidiumConnect.setSamResponse(afidiumConnectSamResponse);
                            return;
                        })
                    );
                }
                return of();
            }));
    }

    public goToAfidiumConnectProfile(): void {
        document.location.href = this.afidiumConnect.getProfileLink();
    }

    public async logout(redirect: boolean): Promise<void> {
        this.removeInfos();
        if (redirect) {
            await this.authRedirectService.redirectToLogin();
        }
    }

    public async logoutAfidiumConnect(redirect: boolean = true): Promise<void> {
        this.initAfidiumConnect();
        if (this.afidiumConnect.isConfigValid() && this.afidiumConnect.isOAuthResponseValid()) {
            const headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', this.afidiumConnect.getSamAuthorizationHeader());
            this.http.get(this.afidiumConnect.getLogoutUrl(), { headers }).subscribe({
                complete: async () => {
                    await this.logout(redirect);
                }
            });
        } else {
            // force remove info + redirect if not possible to logout
            await this.logout(redirect);
        }
    }

    public getXsecurity(model: any): Observable<any> | string {
        const body = JSON.stringify(model);
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const options = { headers };
        this.http.post(this.xsecurityPath, body, options)
            .subscribe(
                (res: any) => {
                    this.getToken(res);
                },
                (error: any): any => {
                    this.handleError(error);
                });
        return '';
    }

    /**
     * fill localStorage for another instance of our apps with login information from one of our apps
     * @param destinationInstanceUrl : this arg wait for an url containing the informations of an instance as we can find using this.instanceService.fullUrl
     * @example for destinationInstanceUrl : https://validation.afidium.com/nightly/sasha
     */
    public shareloginAcrossInstance(destinationInstanceUrl: string): void{
        const destinationUrlParts: string[] = destinationInstanceUrl
            .split('/')
            .slice(-2);
        const destinationLocalStoragePrefix: string = (`/${destinationUrlParts[0]}/`);
        const currentUrl: string = location.hostname === "localhost" ? "/" : this.instanceService.url;
        const currentXsecurity: string = localStorage.getItem(`${currentUrl}_xsecurity`);
        const currentUserInformations: string = localStorage.getItem(`${currentUrl}_userInformations`);
        sessionStorage.setItem('$instance_url', destinationLocalStoragePrefix);
        localStorage.setItem(`${destinationLocalStoragePrefix}_token`, xtoken);
        localStorage.setItem(`${destinationLocalStoragePrefix}_xsecurity`, currentXsecurity);
        localStorage.setItem(`${destinationLocalStoragePrefix}_isLogged`, '1');
        localStorage.setItem(`${destinationLocalStoragePrefix}_userInformations`, currentUserInformations);
    }

    public getXsecurityIP(): void {
        const headers: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
        const options = { headers };
        this.http
            .post(this.xsecurityPath, undefined, options)
            .subscribe((res: any) => {
                    this.getToken(res);
                },
                (error: Error): any => {
                    this.notAuthorized(error);
                });
    }

    /**
     * private function
     * Called from getXsecurity()
     * once the token key is got, stored to localStorage
     */
    public getToken(res: any) {
        let xsecurity: string;
        if (_isString(res)) {
            xsecurity = res;
        } else {
            if (!res.xsecurity) {
                this.notAuthorized(new Error("Token not found"));
                return true;
            }
            xsecurity = res.xsecurity;
        }
        localStorage.setItem(this.lsPrefix + "_xsecurity", xsecurity);
        const body = {};
        const headers: HttpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'cache-control': 'no-cache',
            xsecurity,
            'xapplication': 'WNJAPP'
        });
        const options = { headers };
        return this.http
            .post(this.authPath, body, options)
            .pipe(
                map((response: any) => response.data))
            .subscribe((token: string) => {
                    this.storeToken(token);
                    delete this.response.display.message;
                    // then redirect
                    this.redirect();
                },
                (): any => {
                    this.response.display.message = Yii.t('wui-shared-js', 'Identifiant ou mot de passe incorrects');
                    this.errorLoginMessageSubject.next(this.response.display.message);
                });
    }

    public async handleError(error: Response | any): Promise<void> {
        await this.authRedirectService.redirectToLogin();
    }

    /**
     * Redirects to login page with appropriate error message.
     */
    public async handleApiUnauthorizedError(): Promise<void> {
        console.warn('Authentication token has expired, loging out...');
        this.response.display.message = Yii.t('wui-shared-js', 'Votre session a expiré, veuillez vous reconnecter.');
        await this.handleError(null);
    }

    /**
     * Redirect function
     * launch when good authentication
     * redirect to request app if it exist, home if not
     */
    public async redirect(): Promise<void> {
        if (!this.url || this.url === "home") {
            if (this.redirectUrl && this.redirectUrl !== "home") {
                this.url = this.redirectUrl;
            } else {
                const apps = (wenji.appList || [])
                    .map(name => name.replace(/^app_/, ""))
                    .filter(app => !["all", "home"].includes(app));

                if (apps.length === 1) {
                    this.url = apps[0];
                } else {
                    this.url = "home";
                }
            }
        }
        this.redirectUrl = null;
        await this.router.navigateByUrl(this.url); //use the stored url here
    }


    /**
     * isExpiredTokenThen
     *
     * Test if the token is expired, then re-auth if needed
     */
    public isExpiredTokenThen(): Observable<void> {
        const expTimestamp = jwt_decode(localStorage.getItem(this.lsPrefix + '_token')).exp;
        const nowTimestamp = Math.round(+new Date() / 1000);
        if (expTimestamp > nowTimestamp) {
            return observableOf({}).pipe(map(() => {
                //
            }));

        } else {

            const body = {};

            const headers: HttpHeaders = new HttpHeaders({
                'Content-Type': 'application/json',
                'cache-control': 'no-cache',
                'xsecurity': localStorage.getItem(this.lsPrefix + '_xsecurity'),
                'xapplication': 'WNJAPP'
            });

            const options = { headers };

            return this.http.post(this.authPath, body, options).pipe(
                map((res: any) => {
                    this.storeToken(res.data);
                }));
        }

    }


    public getOsmanRequester(): Observable<IRequester> {
        return this.osmanRequesterCache ?
            of(this.osmanRequesterCache) :
            forkJoin([
                this.osmanCompanyResource.getCompany(wenji.watabi.blocks.companyCode),
                this.samResourceService.getAgentByLogin(wenji.watabi.user.login)
            ])
                .pipe(
                    map(([company, agent]: [IOsmanCompany, ISamAgent]): IRequester => ({
                        company: {id: company.id},
                        user: agent.link.dusr_id_user,
                    })),
                    tap((requester: IRequester) => {
                        this.osmanRequesterCache = requester;
                    })
                );
    }

    public checkTokenExp(): boolean {
        //get the Token Expiration timestamps
        const key: string = `${this.lsPrefix}_token`;

        if (!localStorage.getItem(key)) { //if no userInformations, it means not logged
            return false;
        }

        const token: string = localStorage.getItem(key);

        try {
            const tokenExp: number = jwt_decode(token).exp;
            if ((Math.floor(Date.now() / 1000) > tokenExp)) { // if expired
                this.logoutAfidiumConnect(false);
                return false;
            }

            if (!sessionStorage.getItem("userInformations")) {
                this.storeToken(token);
            }

        } catch (e) {
            console.warn(e);
            this.removeInfos();
            return false;
        }

        return true;
    }

    public loadUserInformations(): void {
        this.isLogged = this.userAuthService.loadUserInformations();
    }

    public storeToken(token: string): void {
        this.userAuthService.storeToken(token);
    }

    public storeAfidiumConnectToken(): void {
        this.userAuthService.storeToken(this.afidiumConnect.getSamToken());
        this.userAuthService.setStorageOAuthToken(this.afidiumConnect.getOAuthToken());
        this.userAuthService.setStorageOAuthTokenType(this.afidiumConnect.getOAuthTokenType());
    }

    public removeInfos(): void {
        this.userAuthService.removeInfos();
        this.isLogged = false;
    }

    private async notAuthorized(error: Error): Promise<void> {
        this.response.display.message = Yii.t('wui-shared-js', 'Adresse IP non autorisée');
        await this.handleError(error);
    }
}
